"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Service = {
  number: string;
  title: string;
  descriptor: string;
  body: string;
  includes: string[];
  pricing: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
const services: Service[] = [{
  number: "N°.01",
  title: "Precision Cutting",
  descriptor: "Architectural shapes tailored to your unique bone structure.",
  body: "Our signature approach to cutting focuses on the intersection of geometry and movement. We analyze your hair's natural growth patterns and texture to create a bespoke silhouette that maintains its integrity long after you leave the chair, ensuring effortless daily styling.",
  includes: ["Consultation & Face-Shape Analysis", "Custom Precision Cut", "Texturizing & Weight Removal", "Blow-Dry Styling Lesson", "Professional Product Recommendations"],
  pricing: "STARTING AT $120",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/precision_cut.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/precision_cut.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/precision_cut.png-wvc-sizes",
  alt: "Modern precision haircut with clean lines and healthy texture"
}, {
  number: "N°.02",
  title: "Luminous Balayage",
  descriptor: "Sun-kissed dimension that grows out with grace.",
  body: "We treat color as a painter treats canvas, hand-painting light into your hair to mimic the natural way the sun illuminates your features. Our balayage technique is designed for longevity, providing a seamless, low-maintenance transition that enhances your natural base.",
  includes: ["Custom Color Placement", "Gloss & Toning Treatment", "Bond-Building Protection", "Root Smudging for Depth", "Hydrating Finishing Mask"],
  pricing: "STARTING AT $250",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/balayage_color.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/balayage_color.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/balayage_color.png-wvc-sizes",
  alt: "Vibrant balayage color transformation with soft highlights"
}, {
  number: "N°.03",
  title: "Restorative Rituals",
  descriptor: "Deep nourishment for hair that feels as good as it looks.",
  body: "A meditative experience designed to restore the vitality of your hair from the inside out. Using high-performance botanical infusions, we address dryness, breakage, and scalp health, leaving your hair with a mirror-like shine and unparalleled softness.",
  includes: ["Scalp Detox & Exfoliation", "Deep-Conditioning Infusion", "Protein Repair Treatment", "Steam-Activated Hydration", "Relaxing Scalp Massage"],
  pricing: "STARTING AT $85",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/hair_treatment.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/hair_treatment.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/hair_treatment.webp-wvc-sizes",
  alt: "Luxurious hair treatment session with botanical products"
}];
function ServiceBlock({
  service,
  index
}: {
  service: Service;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isReversed = index % 2 === 1;
  return <div ref={ref} data-index={index} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-0 items-center">
        {}
        <div className={`lg:col-span-6 ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
          <div className="aspect-[4/5] w-full overflow-hidden rounded-lg">
            <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
        </div>

        {}
        <div className={`lg:col-span-6 ${isReversed ? "lg:order-1 lg:pr-20" : "lg:order-2 lg:pl-20"}`}>
          <div className="max-w-xl">
            <p className="font-mono text-xs tracking-widest uppercase mb-6" style={{
            color: "var(--primary)"
          }}>
              {service.number}
            </p>
            <h3 className="font-serif font-default text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] text-foreground mb-5">
              {service.title}
            </h3>
            <p className="font-serif italic text-xl text-foreground/70 mb-8 leading-snug">
              {service.descriptor}
            </p>
            <p className="text-[17px] leading-relaxed mb-10" style={{
            color: "var(--foreground)",
            opacity: 0.8
          }}>
              {service.body}
            </p>

            <div className="mb-10">
              <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-5">
                Includes
              </p>
              <ul className="space-y-3">
                {service.includes.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-[15px] text-foreground/85">
                    <span className="mt-2 inline-block h-1 w-1 rounded-lg shrink-0" style={{
                  backgroundColor: "var(--primary)"
                }} aria-hidden="true" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>

            <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-8">
              {service.pricing}
            </p>

            <Button asChild size="lg" className="rounded-lg px-7 h-12 text-sm tracking-wide bg-primary text-primary-foreground">
              <Link to="/BookPage">
                Book your session
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>;
}
export default function ArtistryTechniques() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="794" id="artistry-techniques" className="bg-background text-foreground ![padding:128px_0px_128px_0px]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {}
        <div ref={introRef} className={`text-center mb-24 lg:mb-32 transition-all duration-1000 ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-mono text-xs sm:text-sm tracking-[0.25em] uppercase mb-10" style={{
          color: "var(--primary)"
        }}>SERVICES / 2026</p>
          <h2 className="font-serif font-default text-foreground leading-[0.95] mb-10" style={{
          fontSize: "clamp(4rem, 10vw, 8rem)"
        }}>
            Velvet,{" "}
            <span className="italic font-light text-foreground/85">refined.</span>
          </h2>
          <p className="mx-auto text-foreground/70 text-lg leading-relaxed" style={{
          maxWidth: "60ch"
        }}>
            Expert hair artistry tailored to your lifestyle, ensuring every visit leaves you feeling renewed and unmistakably yourself.
          </p>
        </div>

        {}
        <div className="space-y-24 lg:space-y-[120px]">
          {services.map((service, i) => <div key={i} data-index={i}>
              <ServiceBlock service={service} index={i} />
              {i < services.length - 1 && <div className="mt-24 lg:mt-[120px] flex items-center justify-center gap-6">
                  <div className="h-px w-24 sm:w-32" style={{
              backgroundColor: "var(--border)"
            }} />
                  <span className="inline-block h-1.5 w-1.5 rotate-45" style={{
              backgroundColor: "var(--primary)"
            }} aria-hidden="true" />
                  <div className="h-px w-24 sm:w-32" style={{
              backgroundColor: "var(--border)"
            }} />
                </div>}
            </div>)}
        </div>
      </div>
    </section>;
}